import { Trash2, X, Check, Loader2 } from "lucide-react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface ModalConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  description: string | null;
  title?: string;
  note?: string;
  confirmIcon?: "delete" | "confirm";
  confirmButtonColor?: "red" | "primary";
  cancelLabel?: string;
  confirmLabel?: string;
  isConfirming?: boolean;
  children?: React.ReactNode;
}

const ModalConfirmation: React.FC<ModalConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  description,
  title,
  note,
  confirmIcon = "delete",
  confirmButtonColor = "red",
  cancelLabel,
  confirmLabel,
  isConfirming = false,
  children,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (isConfirming) return;
        onConfirm();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isConfirming, onConfirm, onClose]);

  if (!isOpen) return null; // Don't render if closed

  const getConfirmButtonStyle = () => {
    if (confirmButtonColor === "primary") {
      return { background: 'linear-gradient(to right, var(--color-primary), var(--color-secondary))' };
    }
    return undefined;
  };

  const getConfirmButtonClass = () => {
    if (confirmButtonColor === "primary") {
      return "text-white rounded-full px-6 py-3 hover:opacity-90 transition-opacity";
    }
    return "bg-red-600 hover:bg-red-700 rounded-full text-white px-6 py-3";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm z-50">
      <div className="bg-white p-8 rounded-md shadow-lg w-1/3 sm:w-2/5 lg:w-1/3">
        <h2 className="text-xl mb-4">
          {title || t(`deleteMessage`)} {description && <b>{description}</b>} {!title && "?"}
        </h2>
        {note && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
            {note}
          </p>
        )}
        {children}
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            disabled={isConfirming}
            title={cancelLabel || t("no")}
            aria-label={cancelLabel || t("no")}
            className={`mr-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-600 text-2xl text-white hover:bg-gray-700 ${
              isConfirming ? "cursor-not-allowed opacity-60" : ""
            }`}
          >
            <X />
          </button>
          <button
            onClick={onConfirm}
            disabled={isConfirming}
            title={confirmLabel || t("yes")}
            aria-label={confirmLabel || t("yes")}
            className={`${getConfirmButtonClass()} ${isConfirming ? "cursor-not-allowed opacity-80" : ""}`}
            style={getConfirmButtonStyle()}
          >
            {isConfirming ? (
              <Loader2 className="animate-spin text-2xl" />
            ) : confirmIcon === "delete" ? (
              <Trash2 className="text-2xl" />
            ) : (
              <Check className="text-2xl" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalConfirmation;
